import { SvgIcon } from "@tql/ui-components-react/kendo/common";
import type { InputChangeEvent } from "@tql/ui-components-react/kendo/inputs";
import { Checkbox, Input } from "@tql/ui-components-react/kendo/inputs";
import { Button } from "@tql/ui-components-react/tql/buttons";
import {
	pencil_01,
	searchSmLine,
	trash_01,
} from "@tql/ui-components-react/untitled/icons";

import {
	AddressBookCard,
	AddressBookCardSkeleton,
	addressBookCardStyles,
} from "~/shared/components/address-book-card/address-book-card.tsx";
import { useSearchableInfiniteList } from "~/shared/hooks/use-searchable-infinite-list.ts";
import type { SubGroupKey } from "~/shared/types/quote-details-types.ts";
import { formatPhoneNumber } from "~/shared/utils/string-fns.ts";

import type { SelectDescriptor } from "../../hooks/use-address-selection.ts";
import type { AddressBookModalRow } from "../../types.ts";
import styles from "./mobile-address-list.module.css";

type MobileAddressListProps = {
	data: AddressBookModalRow[];
	isLoading: boolean;
	type: SubGroupKey;
	select: SelectDescriptor;
	handleSelect: (id: string | number) => void;
	handleDeselect: (id: string | number) => void;
	handleRemoveFromList: (dataItem: AddressBookModalRow) => void;
	onEdit?: (dataItem: AddressBookModalRow) => void;
};

const LOADING_CARD_COUNT = 5;
const EMPTY_VALUE = "\u2014";

// Null/blank means no value. Any other content is valid, including non-ASCII scripts
// (the address-book schema and desktop grid accept and display them unmodified).
const resolveValue = (value: string | null | undefined) => {
	const trimmed = value?.trim();
	return trimmed || EMPTY_VALUE;
};

const SEPARATOR_CHARS = new Set<string | undefined>([" ", ",", "-"]);

const stripEdgeSeparators = (value: string) => {
	let start = 0;
	let end = value.length;
	while (start < end && SEPARATOR_CHARS.has(value[start])) start++;
	while (end > start && SEPARATOR_CHARS.has(value[end - 1])) end--;
	return value.slice(start, end);
};

// City/state/zip and hours arrive pre-composed with ", " / " - " separators, so a value
// left after stripping those separators from both edges means real content exists;
// otherwise only the separator remains and it should read as empty.
const resolveComposedValue = (value: string | null | undefined) => {
	const trimmed = value?.trim();
	if (!trimmed) return EMPTY_VALUE;
	return stripEdgeSeparators(trimmed) ? trimmed : EMPTY_VALUE;
};

const getSearchableFields = (item: AddressBookModalRow) => [
	item.companyName,
	item.address1,
	item.address2,
	item.cityStateZip,
	item.contactName,
	item.contactPhone,
	item.contactExtension,
	item.hoursOpen,
];

export const MobileAddressList = ({
	data,
	isLoading,
	type,
	select,
	handleSelect,
	handleDeselect,
	handleRemoveFromList,
	onEdit,
}: MobileAddressListProps) => {
	const {
		searchTerm: term,
		setSearchTerm: setTerm,
		visibleItems: visibleAddresses,
		filteredCount: matchCount,
		handleScroll: onListScroll,
	} = useSearchableInfiniteList({
		items: data,
		getSearchableFields,
		pageSize: 25,
	});

	const hoursLabel = type === "pickup" ? "Shipper Hours" : "Receiver Hours";

	const handleSearchChange = (event: InputChangeEvent) => {
		setTerm(String(event.value ?? ""));
	};

	return (
		<div className={styles.container}>
			<div className={styles.searchWrapper}>
				<Input
					aria-label={"Search address book"}
					className={styles.searchInput}
					data-testid={"address-book-search-input"}
					disabled={isLoading}
					onChange={handleSearchChange}
					placeholder={"Search..."}
					value={term}
				/>
				<SvgIcon className={styles.searchIcon} icon={searchSmLine} />
			</div>
			<div
				aria-busy={isLoading}
				className={styles.listScrollRegion}
				data-testid={"address-book-list-scroll-region"}
				onScroll={onListScroll}
			>
				{isLoading ? (
					Array.from({ length: LOADING_CARD_COUNT }, (_, index) => (
						<AddressBookCardSkeleton key={`loading-${index}`} />
					))
				) : (
					<>
						{matchCount === 0 ? (
							<div className={styles.emptyMessage}>No Records Available</div>
						) : null}
						{visibleAddresses.map((item) => {
							const isSelected = select[item.addressBookItemId] === true;
							return (
								<AddressBookCard
									key={item.addressBookItemId}
									actions={
										<>
											{onEdit ? (
												<Button
													aria-label={`Edit ${item.companyName ?? "address"}`}
													data-testid={"address-card-edit-button"}
													disabled={item.isLegacy}
													onClick={() => onEdit(item)}
													svgIcon={pencil_01}
													variant={"tertiary"}
												/>
											) : null}
											<Button
												aria-label={`Delete ${item.companyName ?? "address"}`}
												data-testid={"address-card-delete-button"}
												disabled={item.isLegacy}
												onClick={() => {
													handleRemoveFromList(item);
												}}
												svgIcon={trash_01}
												variant={"dangerTertiary"}
											/>
										</>
									}
									data-testid={"address-card"}
									leading={
										<Checkbox
											aria-label={`Select ${item.companyName ?? "address"}`}
											checked={isSelected}
											data-testid={"address-card-select-checkbox"}
											onChange={(event: { value: boolean }) => {
												if (event.value) {
													handleSelect(item.addressBookItemId);
												} else {
													handleDeselect(item.addressBookItemId);
												}
											}}
										/>
									}
									title={resolveValue(item.companyName)}
								>
									<div className={styles.cardBody}>
										<div className={styles.column}>
											<div className={addressBookCardStyles.fieldGroup}>
												<div className={addressBookCardStyles.fieldLabel}>
													Address
												</div>
												<div data-address-lines={true}>
													<div>{resolveValue(item.address1)}</div>
													{item.address2 ? <div>{item.address2}</div> : null}
													<div>{resolveComposedValue(item.cityStateZip)}</div>
												</div>
											</div>
											<div className={addressBookCardStyles.fieldGroup}>
												<div className={addressBookCardStyles.fieldLabel}>
													Contact
												</div>
												<div>{resolveValue(item.contactName)}</div>
											</div>
										</div>
										<div className={styles.column}>
											<div className={addressBookCardStyles.fieldGroup}>
												<div className={addressBookCardStyles.fieldLabel}>
													Phone
												</div>
												<div>
													{resolveValue(formatPhoneNumber(item.contactPhone))}
												</div>
											</div>
											<div className={addressBookCardStyles.fieldGroup}>
												<div className={addressBookCardStyles.fieldLabel}>
													Ext
												</div>
												<div>{resolveValue(item.contactExtension)}</div>
											</div>
											<div className={addressBookCardStyles.fieldGroup}>
												<div className={addressBookCardStyles.fieldLabel}>
													{hoursLabel}
												</div>
												<div>{resolveComposedValue(item.hoursOpen)}</div>
											</div>
										</div>
									</div>
								</AddressBookCard>
							);
						})}
					</>
				)}
			</div>
		</div>
	);
};
